import { useState } from 'react';
import useCallbackRef from './useCallbackRef';
import { useResizeEventListenerOnElement } from './useEventListenerOnElement';
import useResizeObserver from './useResizeObserver';

type DocumentAndElementHeights<T extends HTMLElement> = {
  documentHeight: number;
  elementHeight: number;
  elementRef: (node: T | null) => void;
};

const useDynamicDocumentAndElementHeights = <
  T extends HTMLElement,
>(): DocumentAndElementHeights<T> => {
  const [documentHeight, setDocumentHeight] = useState<number>(
    document.documentElement.clientHeight,
  );
  const [elementHeight, setElementHeight] = useState<number>(0);
  const [elementContent, setElementContent] = useState<T>();

  const elementRef = useCallbackRef((node: T) => {
    setElementHeight(node.getBoundingClientRect().height);
    setElementContent(node);
  });

  useResizeEventListenerOnElement(document.documentElement, () => {
    setDocumentHeight(document.documentElement.clientHeight);
  });

  useResizeObserver(elementContent, () => {
    const height = elementContent?.clientHeight || 0;
    setElementHeight(height);
  });

  return {
    documentHeight,
    elementHeight,
    elementRef,
  };
};

export default useDynamicDocumentAndElementHeights;
